/**
 * 2048 PK 跨端输入控制器 (Input Controller)
 * 职责：
 * 1. 键盘监听 (ArrowKeys, WASD)
 * 2. 移动端触摸滑动手势 (Touch Swipes with Angle/Distance threshold)
 * 3. 屏幕虚拟十字键与手柄适配
 * 4. 防止移动端页面下拉刷新与原生滚动穿透
 */

import type { MoveDirection } from "../../shared/protocol";

export type MoveHandler = (dir: MoveDirection) => void;

export class InputController {
  private targetEl: HTMLElement;
  private onMove: MoveHandler;
  private startX = 0;
  private startY = 0;
  private isTracking = false;
  private hasTriggeredSwipe = false;
  private activePointerId: number | null = null;
  private minSwipeDistance = 18; // 灵敏短促拇指轻拂阈值 (px)

  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private touchStartHandler: ((e: TouchEvent) => void) | null = null;
  private touchMoveHandler: ((e: TouchEvent) => void) | null = null;
  private touchEndHandler: ((e: TouchEvent) => void) | null = null;
  private pointerDownHandler: ((e: PointerEvent) => void) | null = null;
  private pointerMoveHandler: ((e: PointerEvent) => void) | null = null;
  private pointerUpHandler: ((e: PointerEvent) => void) | null = null;

  constructor(targetEl: HTMLElement, onMove: MoveHandler) {
    this.targetEl = targetEl;
    this.onMove = onMove;
    this.bindEvents();
  }

  /**
   * 判断触摸/指针起点是否位于表单输入、按钮或聊天消息滚动区内（此类区域保留原生点击与滚动）
   */
  private shouldIgnoreGestureTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(
      target.closest(
        "input, textarea, button, .battle-chat-messages, .seat-chat-messages, .pk-modal-backdrop, .pk-settlement-dock"
      )
    );
  }

  private evaluateSwipeDelta(dx: number, dy: number): MoveDirection | null {
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (Math.max(absDx, absDy) < this.minSwipeDistance) {
      return null;
    }
    if (absDx > absDy) {
      return dx > 0 ? "RIGHT" : "LEFT";
    }
    return dy > 0 ? "DOWN" : "UP";
  }

  private bindEvents() {
    // 1. 键盘监听 (ArrowKeys / WASD)
    this.keydownHandler = (e: KeyboardEvent) => {
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA")) {
        return;
      }

      let dir: MoveDirection | null = null;
      switch (e.key) {
        case "ArrowUp":
        case "w":
        case "W":
          dir = "UP";
          break;
        case "ArrowDown":
        case "s":
        case "S":
          dir = "DOWN";
          break;
        case "ArrowLeft":
        case "a":
        case "A":
          dir = "LEFT";
          break;
        case "ArrowRight":
        case "d":
        case "D":
          dir = "RIGHT";
          break;
      }

      if (dir) {
        e.preventDefault();
        this.onMove(dir);
      }
    };
    window.addEventListener("keydown", this.keydownHandler);

    // 2. 原生 Touch 手势监听（支持手指滑过阈值即刻响应，无需等抬手，支持横屏全战场顺滑盲操）
    this.touchStartHandler = (e: TouchEvent) => {
      if (e.touches.length !== 1 || this.shouldIgnoreGestureTarget(e.target)) return;
      this.startX = e.touches[0].clientX;
      this.startY = e.touches[0].clientY;
      this.isTracking = true;
      this.hasTriggeredSwipe = false;
    };

    this.touchMoveHandler = (e: TouchEvent) => {
      if (!this.isTracking || e.touches.length < 1) return;
      if (e.cancelable) {
        e.preventDefault();
      }
      if (this.hasTriggeredSwipe) return;

      const dx = e.touches[0].clientX - this.startX;
      const dy = e.touches[0].clientY - this.startY;
      const dir = this.evaluateSwipeDelta(dx, dy);
      if (dir) {
        this.hasTriggeredSwipe = true;
        this.onMove(dir);
      }
    };

    this.touchEndHandler = (e: TouchEvent) => {
      if (!this.isTracking) return;
      this.isTracking = false;
      if (this.hasTriggeredSwipe || e.changedTouches.length < 1) return;

      const dx = e.changedTouches[0].clientX - this.startX;
      const dy = e.changedTouches[0].clientY - this.startY;
      const dir = this.evaluateSwipeDelta(dx, dy);
      if (dir) {
        this.hasTriggeredSwipe = true;
        this.onMove(dir);
      }
    };

    this.targetEl.addEventListener("touchstart", this.touchStartHandler, { passive: true });
    this.targetEl.addEventListener("touchmove", this.touchMoveHandler, { passive: false });
    this.targetEl.addEventListener("touchend", this.touchEndHandler, { passive: true });
    this.targetEl.addEventListener("touchcancel", this.touchEndHandler, { passive: true });

    // 3. Pointer 手势兼容（支持桌面鼠标拖拽滑动、触控板与模拟器 Pointer 事件）
    this.pointerDownHandler = (e: PointerEvent) => {
      if (e.pointerType === "touch") return; // 真机 touch 已由上方 touch 事件精准接管
      if (e.button !== 0 || this.shouldIgnoreGestureTarget(e.target)) return;
      this.activePointerId = e.pointerId;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.isTracking = true;
      this.hasTriggeredSwipe = false;
    };

    this.pointerMoveHandler = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (!this.isTracking || e.pointerId !== this.activePointerId || this.hasTriggeredSwipe) {
        return;
      }
      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      const dir = this.evaluateSwipeDelta(dx, dy);
      if (dir) {
        this.hasTriggeredSwipe = true;
        this.onMove(dir);
      }
    };

    this.pointerUpHandler = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (!this.isTracking || e.pointerId !== this.activePointerId) return;
      this.isTracking = false;
      this.activePointerId = null;
      if (this.hasTriggeredSwipe) return;

      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      const dir = this.evaluateSwipeDelta(dx, dy);
      if (dir) {
        this.hasTriggeredSwipe = true;
        this.onMove(dir);
      }
    };

    this.targetEl.addEventListener("pointerdown", this.pointerDownHandler);
    window.addEventListener("pointermove", this.pointerMoveHandler);
    window.addEventListener("pointerup", this.pointerUpHandler);
    window.addEventListener("pointercancel", this.pointerUpHandler);
  }

  public destroy() {
    if (this.keydownHandler) {
      window.removeEventListener("keydown", this.keydownHandler);
      this.keydownHandler = null;
    }
    if (this.touchStartHandler) {
      this.targetEl.removeEventListener("touchstart", this.touchStartHandler);
    }
    if (this.touchMoveHandler) {
      this.targetEl.removeEventListener("touchmove", this.touchMoveHandler);
    }
    if (this.touchEndHandler) {
      this.targetEl.removeEventListener("touchend", this.touchEndHandler);
      this.targetEl.removeEventListener("touchcancel", this.touchEndHandler);
    }
    if (this.pointerDownHandler) {
      this.targetEl.removeEventListener("pointerdown", this.pointerDownHandler);
    }
    if (this.pointerMoveHandler) {
      window.removeEventListener("pointermove", this.pointerMoveHandler);
    }
    if (this.pointerUpHandler) {
      window.removeEventListener("pointerup", this.pointerUpHandler);
      window.removeEventListener("pointercancel", this.pointerUpHandler);
    }
  }
}

